import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Users, Trash2, Mail, Edit, ShieldCheck, Search, Building2, KeyRound, Copy, MessageCircle, Link2Off, IdCard, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import {
  listEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getSetupStatus,
  
} from "@/lib/api/employees.functions";
import { getPermissions } from "@/lib/api/employees.functions";
import { resetStaffPin, setStaffActive } from "@/lib/api/staff-pin.functions";
import { createStaffAccessLink, revokeStaffAccessLink, listStaffAccessLinks } from "@/lib/api/staff-access.functions";
import { ROLE_OPTIONS } from "@/lib/permissions-catalog";
import { maskCPF, isValidCPF, onlyDigitsCPF } from "@/lib/cpf";
import { getPublicAppOrigin } from "@/lib/public-origin";

export const Route = createFileRoute("/_authenticated/m/funcionarios")({
  component: EmployeesPage,
});

type Employee = {
  id: string;
  email: string | null;
  login: string | null;
  name: string | null;
  role: string;
  cpf: string | null;
  hasPin: boolean;
  photo_url: string | null;
  isActive?: boolean;
  isBootstrapAdmin?: boolean;
};


type LinkInfo = Record<string, { expires_at: string | null; expired: boolean }>;

const LINK_VALIDITY_OPTIONS = [
  { value: "7", label: "7 dias" },
  { value: "30", label: "30 dias" },
  { value: "90", label: "90 dias" },
  { value: "never", label: "Sem expiração" },
];

function EmployeesPage() {
  const navigate = useNavigate();
  const list = useServerFn(listEmployees);
  const create = useServerFn(createEmployee);
  const update = useServerFn(updateEmployee);
  const del = useServerFn(deleteEmployee);
  const resetPin = useServerFn(resetStaffPin);
  const toggleActive = useServerFn(setStaffActive);
  const makeLink = useServerFn(createStaffAccessLink);
  const revokeLink = useServerFn(revokeStaffAccessLink);
  const fetchLinks = useServerFn(listStaffAccessLinks);
  const status = useServerFn(getSetupStatus);

  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<LinkInfo>({});
  const [linkValidity, setLinkValidity] = useState("30");

  const expiresInDays = linkValidity === "never" ? null : Number(linkValidity);

  const buildAccessUrl = (token: string) => `${getPublicAppOrigin(window.location.origin)}/acesso/${token}`;

  const reloadLinks = async () => {
    try {
      const res = await fetchLinks();
      setLinkInfo((res as any).links ?? {});
    } catch {
      /* silencioso */
    }
  };

  const onCopyLink = async (emp: { id: string; name: string | null }) => {
    try {
      const { token } = await makeLink({ data: { staffId: emp.id, expiresInDays } });
      await navigator.clipboard.writeText(buildAccessUrl(token));
      toast.success(`Link de acesso de ${emp.name ?? "colaborador"} copiado!`);
      reloadLinks();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao gerar link");
    }
  };

  const onSendWhatsapp = async (emp: { id: string; name: string | null }) => {
    try {
      const { token } = await makeLink({ data: { staffId: emp.id, expiresInDays } });
      const msg = `Olá ${emp.name ?? ""}! Acesse o app FutBux por este link e entre com seu PIN: ${buildAccessUrl(token)}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
      reloadLinks();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao gerar link");
    }
  };

  const onRenewLink = async (emp: { id: string; name: string | null }) => {
    try {
      await makeLink({ data: { staffId: emp.id, regenerate: true, expiresInDays } });
      toast.success(
        `Novo link gerado para ${emp.name ?? "colaborador"} (${linkValidity === "never" ? "sem expiração" : `${linkValidity} dias`}). O link anterior perdeu a validade.`,
      );
      reloadLinks();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao gerar link");
    }
  };

  const onRevokeLink = async (emp: { id: string; name: string | null }) => {
    if (!confirm(`Revogar o link de acesso de ${emp.name ?? "colaborador"}? Ele deixará de funcionar imediatamente.`)) return;
    try {
      await revokeLink({ data: { staffId: emp.id } });
      toast.success("Link revogado. Gere um novo quando precisar.");
      reloadLinks();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao revogar link");
    }
  };

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [cpf, setCpf] = useState("");
  const [pin, setPin] = useState("");
  const [role, setRole] = useState("administrativo");
  const [saving, setSaving] = useState(false);
  const [customRoles, setCustomRoles] = useState<{ value: string; label: string }[]>([]);
  const fetchPerms = useServerFn(getPermissions);
  const allRoleOptions = [
    ...ROLE_OPTIONS,
    ...customRoles.filter((c) => !ROLE_OPTIONS.some((r) => r.value === c.value)),
  ];

  const onToggleActive = async (emp: Employee) => {
    try {
      await toggleActive({ data: { staffId: emp.id, active: emp.isActive === false } });
      toast.success(emp.isActive === false ? "Perfil reativado." : "Perfil inativado.");
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Não foi possível alterar o status.");
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const st = await status();
      if (!st.hasTrainingCenter) {
        setNeedsSetup(true);
        setEmployees([]);
        return;
      }
      setNeedsSetup(false);
      const res = await list();
      setEmployees(res.employees as Employee[]);
      setOwnerId((res as any).ownerId ?? null);
      reloadLinks();
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (msg.includes("Unauthorized") || msg.includes("authorization")) {
        navigate({ to: "/auth", replace: true });
        return;
      }
      toast.error(msg || "Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    reload();
    (async () => {
      try {
        const res = await fetchPerms();
        const baseValues = new Set(ROLE_OPTIONS.map((r) => r.value));
        const extras = Object.keys(res.permissions ?? {})
          .filter((k) => !baseValues.has(k) && k !== "proprietario")
          .map((k) => ({
            value: k,
            label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          }));
        setCustomRoles(extras);
      } catch { /* noop */ }
    })();
  }, []);

  const resetForm = () => {
    setEditId(null);
    setName("");
    setLogin("");
    setCpf("");
    setPin("");
    setRole("administrativo");
  };

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (e: Employee) => {
    setEditId(e.id);
    setName(e.name ?? "");
    setLogin(e.login ?? "");
    setCpf(maskCPF(e.cpf ?? ""));
    setPin("");
    setRole(e.role);
    setModalOpen(true);
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const cpfDigits = onlyDigitsCPF(cpf);
    if (cpfDigits && !isValidCPF(cpfDigits)) {
      toast.error("CPF inválido. Confira os 11 dígitos.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await update({ data: { userId: editId, name, login, pin: pin || undefined, role, cpf: cpfDigits || null } });
        toast.success("Funcionário atualizado!");
      } else {
        await create({ data: { name, login, pin, role, cpf: cpfDigits || null } });
        toast.success("Funcionário cadastrado!");
      }
      setModalOpen(false);
      resetForm();
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm("Excluir este funcionário?")) return;
    try {
      await del({ data: { userId: id } });
      toast.success("Funcionário excluído");
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir");
    }
  };

  const onResetPin = async (id: string, nm: string | null) => {
    if (!confirm(`Resetar o PIN de ${nm ?? "este funcionário"}? Ele precisará definir um novo PIN no próximo acesso.`)) return;
    try {
      await resetPin({ data: { staffId: id } });
      toast.success("PIN resetado. O funcionário definirá um novo no próximo login.");
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao resetar PIN");
    }
  };

  const filtered = employees.filter(
    (e) =>
      (e.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (e.login ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  if (needsSetup) {
    return (
      <div className="max-w-xl mx-auto">
        <Card>
          <CardContent className="p-8 space-y-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Cadastre seu Centro de Treinamento</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Antes de adicionar funcionários, precisamos dos dados completos do seu CT.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate({ to: "/m/$slug", params: { slug: "dados-ct" } })}
            >
              Ir para o cadastro do Centro de Treinamento
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> Equipe e Funcionários
          </h1>
          <p className="text-muted-foreground text-sm">
            Gerencie os acessos, cargos e senhas da sua equipe.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => navigate({ to: "/m/$slug", params: { slug: "configurar-perfil" } })}>
            <ShieldCheck className="w-4 h-4 mr-2" /> Configurar Perfil
          </Button>
          <Button onClick={openCreate}>
            <UserPlus className="w-4 h-4 mr-2" /> Novo Funcionário
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar funcionário..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Validade do link</Label>
          <Select value={linkValidity} onValueChange={setLinkValidity}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LINK_VALIDITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>


      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[250px] bg-muted/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center bg-card rounded-xl border border-dashed">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium">Nenhum funcionário encontrado</h3>
          <p className="text-muted-foreground mt-1">Clique no botão acima para adicionar membros à sua equipe.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((emp) => {
            const isOwner = ownerId === emp.id;
            const isFixed = !!emp.isBootstrapAdmin;
            return (
            <Card key={emp.id} className={`overflow-hidden ${emp.isActive === false ? "opacity-60" : ""}`}>
              <CardContent className="p-6 pt-8 flex flex-col items-center text-center">
                <Avatar className="h-20 w-20 mb-4">
                  <AvatarFallback className="text-2xl font-bold bg-primary/10 text-primary">
                    {emp.name?.charAt(0).toUpperCase() ?? "?"}
                  </AvatarFallback>
                </Avatar>
                <h3 className="font-bold text-lg leading-tight mb-1 line-clamp-1">{emp.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1 truncate w-full justify-center">
                  <Mail className="h-3 w-3 shrink-0" />
                  {isOwner ? (emp.email ?? "") : isFixed ? "Acesso somente pelo card fixo" : (emp.login ?? "—")}
                </p>
                {!isFixed && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4 justify-center">
                    <IdCard className="h-3 w-3 shrink-0" />
                    {emp.cpf ? maskCPF(emp.cpf) : "CPF não informado"}
                  </p>
                )}
                <div className="flex flex-wrap gap-1 justify-center">
                  <Badge variant={isOwner ? "default" : "secondary"} className="text-xs">
                    {isOwner ? "Proprietário" : (allRoleOptions.find((r) => r.value === emp.role)?.label || emp.role)}
                  </Badge>
                  {isFixed && (
                    <Badge variant="outline" className="text-xs">Card fixo</Badge>
                  )}
                  {emp.isActive === false && (
                    <Badge variant="destructive" className="text-xs">Inativo</Badge>
                  )}
                </div>
                {isFixed && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    {emp.hasPin
                      ? "PIN cadastrado"
                      : "SEM PIN CADASTRADO — DEFINA UM PIN DE 4 DÍGITOS NO PRIMEIRO ACESSO"}
                  </p>
                )}
                {!isOwner && !isFixed && (
                  <>
                    <p className="mt-3 text-xs flex items-center gap-1 justify-center text-muted-foreground">
                      <CalendarClock className="h-3 w-3 shrink-0" />
                      {(() => {
                        const info = linkInfo[emp.id];
                        if (!info) return "Sem link ativo";
                        if (info.expired) return "Link expirado";
                        if (!info.expires_at) return "Link ativo (sem expiração)";
                        return `Link válido até ${new Date(info.expires_at).toLocaleDateString("pt-BR")}`;
                      })()}
                    </p>
                    <div className="mt-3 w-full flex flex-wrap gap-1 justify-center">
                      <Button size="sm" variant="secondary" onClick={() => onCopyLink(emp)}>
                        <Copy className="h-4 w-4 mr-1" /> Copiar link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onSendWhatsapp(emp)} title="Enviar por WhatsApp">
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onRenewLink(emp)} title="Gerar novo link (invalida o anterior)">
                        <CalendarClock className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onRevokeLink(emp)} title="Revogar link">
                        <Link2Off className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}

              </CardContent>
              <CardFooter className="bg-muted/30 p-3 flex flex-wrap gap-1 justify-center border-t">
                {!isFixed && (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(emp)}>
                    <Edit className="h-4 w-4 mr-1" /> Editar
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onResetPin(emp.id, emp.name)}
                  title="Resetar PIN"
                >
                  <KeyRound className="h-4 w-4 mr-1" /> Resetar PIN
                </Button>
                {isFixed ? (
                  <Button variant="ghost" size="sm" onClick={() => onToggleActive(emp)}>
                    {emp.isActive === false ? "Reativar" : "Inativar"}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(emp.id)}
                    disabled={isOwner}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Excluir
                  </Button>
                )}
              </CardFooter>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(o) => { setModalOpen(o); if (!o) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Funcionário" : "Cadastrar Funcionário"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="João Silva" />
            </div>
            <div className="space-y-2">
              <Label>CPF <span className="text-xs text-muted-foreground">(opcional)</span></Label>
              <Input
                inputMode="numeric"
                value={cpf}
                onChange={(e) => setCpf(maskCPF(e.target.value))}
                placeholder="000.000.000-00"
              />
            </div>

            <div className="space-y-2">
              <Label>Usuário</Label>
              <Input
                type="text"
                required
                value={login}
                onChange={(e) => setLogin(e.target.value.toLowerCase().replace(/\s+/g, ""))}
                placeholder="joao.silva"
                pattern="[a-z0-9._-]{3,}"
              />
              <p className="text-xs text-muted-foreground">
                O funcionário fará login usando este usuário e o PIN de acesso.
              </p>
            </div>
            <div className="space-y-2">
              <Label>PIN de Acesso {editId && <span className="text-xs text-muted-foreground">(opcional)</span>}</Label>
              <Input
                type="text"
                required={!editId}
                maxLength={4}
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder={editId ? "Deixe em branco para manter" : "0000"}
              />
              <p className="text-xs text-muted-foreground">4 dígitos numéricos usados na tela de seleção de perfil.</p>
            </div>
            <div className="space-y-2">
              <Label>Perfil de Acesso</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allRoleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="pt-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Salvando..." : editId ? "Salvar Alterações" : "Cadastrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
